import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { cable } from "../lair/cable.js"
import { chime } from "../lair/chime.js"
import { chatWhen } from "../lair/chat_time.js"

// A friend's chat message, on whatever page the player is on (ChatChannel).
// The masthead's bubble counts the friends waiting on an answer and the tab's
// title carries the same number, so a game in a background tab still says so;
// both are redrawn from the server's count ("waiting"), never guessed here.
// A card in the corner shows the line itself, except on the chat with that
// friend (which draws it) and on /chat (whose list is reloaded instead).
const SHOW_MS = 20000
const SHOWN = 3
const PREVIEW = 120
const PULSE_MS = 1200
const TITLE_COUNT = /^\(\d+\) /

export default class extends Controller {
  static values = { me: Number }

  connect() {
    this.cards = new Map()
    this.stampIndex()
    this.subscription = cable().subscriptions.create("ChatChannel", { received: (data) => this.received(data) })
  }

  disconnect() {
    this.subscription?.unsubscribe()
    this.subscription = null
    for (const { timer } of this.cards.values()) clearTimeout(timer)
    this.cards.clear()
    clearTimeout(this.pulseTimer)
    this.element.replaceChildren()
  }

  received(data) {
    if (data?.type === "waiting") return this.showWaiting(Number(data.count) || 0)
    if (data?.type !== "message" || data.recipient_id !== this.meValue) return
    if (document.querySelector(`[data-controller~="chat"][data-chat-friend-id-value="${data.sender_id}"]`)) return

    chime("care")
    this.pulse()
    if (document.querySelector("[data-chat-index]")) {
      Turbo.visit(window.location.href, { action: "replace" })
    } else {
      this.show(data)
    }
  }

  showWaiting(count) {
    const bubble = document.getElementById("masthead-chat")
    if (bubble) {
      const number = bubble.querySelector("[data-chat-count]")
      number.textContent = count
      number.hidden = count === 0
      bubble.classList.toggle("is-waiting", count > 0)
      bubble.setAttribute("aria-label", count > 0 ? `Chats, ${count} unread` : "Chats")
    }
    const title = document.title.replace(TITLE_COUNT, "")
    document.title = count > 0 ? `(${count}) ${title}` : title
  }

  pulse() {
    const bubble = document.getElementById("masthead-chat")
    if (!bubble) return
    bubble.classList.remove("is-new")
    void bubble.offsetWidth
    bubble.classList.add("is-new")
    clearTimeout(this.pulseTimer)
    this.pulseTimer = setTimeout(() => bubble.classList.remove("is-new"), PULSE_MS)
  }

  stampIndex() {
    const now = new Date()
    document.querySelectorAll("[data-chat-index] [data-chat-when]").forEach((time) => {
      time.textContent = chatWhen(new Date(time.dateTime), now)
    })
  }

  show(message) {
    this.drop(message.sender_id)

    const href = `/chat/${encodeURIComponent(message.from)}`
    const card = el("div", "ll-chat-toast")
    card.setAttribute("role", "status")
    card.append(el("p", "ll-label ll-label--gold", `Message from ${message.from}`))
    const text = message.text.length > PREVIEW ? `${message.text.slice(0, PREVIEW)}…` : message.text
    const preview = el("a", "ll-chat-toast__text", text)
    preview.href = href
    card.append(preview)

    const actions = el("div", "ll-chat-toast__actions")
    const reply = el("a", "ll-btn ll-btn--primary", "Reply")
    reply.href = href
    const close = el("button", "ll-btn", "Close")
    close.type = "button"
    close.addEventListener("click", () => this.drop(message.sender_id))
    actions.append(reply, close)
    card.append(actions)

    // A card being read or reached for stays until it is let go.
    const hold = () => clearTimeout(this.cards.get(message.sender_id)?.timer)
    const release = () => this.leaveLater(message.sender_id)
    card.addEventListener("pointerenter", hold)
    card.addEventListener("focusin", hold)
    card.addEventListener("pointerleave", release)
    card.addEventListener("focusout", release)

    this.element.append(card)
    this.cards.set(message.sender_id, { node: card, timer: null })
    this.leaveLater(message.sender_id)
    while (this.cards.size > SHOWN) this.drop(this.cards.keys().next().value)
  }

  leaveLater(senderId) {
    const card = this.cards.get(senderId)
    if (!card) return
    clearTimeout(card.timer)
    card.timer = setTimeout(() => this.drop(senderId), SHOW_MS)
  }

  drop(senderId) {
    const card = this.cards.get(senderId)
    if (!card) return
    clearTimeout(card.timer)
    card.node.remove()
    this.cards.delete(senderId)
  }
}

function el(tag, className, text) {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text != null) node.textContent = text
  return node
}
